import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import type { DependencyList } from 'react';
import * as cache from './queryCache';

export interface Resource<T> {
  /** The last answer. It stays while a refresh is under way. */
  data: T | null;
  /** Why there is no data; set only while there is none. */
  error: Error | null;
  /** True only while there is nothing to show yet: a refresh is not loading. */
  loading: boolean;
  reload: () => void;
  /** Replace the answer with one already in hand (a save returned the new state). */
  mutate?: (data: T) => void;
  /** A refresh is under way behind the data on screen. */
  revalidating?: boolean;
  /** A refresh failed and `data` is the older answer (network or server trouble). */
  refreshError?: Error | null;
}

export interface ResourceOptions {
  /**
   * Names what this reads, e.g. `project:${id}:deployments:${envId}`. A keyed
   * resource is cached across visits: the next one shows the last answer at
   * once and refreshes it quietly, and every component reading the key shares
   * one request. Include everything the loader depends on in the key. Leave
   * it out for a resource that belongs to one component instance.
   */
  key?: string;
  /** How long an answer counts as fresh; visiting within it asks nothing. */
  staleMs?: number;
  /**
   * False asks nothing and listens for nothing (what is cached still shows).
   * For an expensive read that only some screens show: the project shell holds
   * the state for all its sections, but must not run a server inspection for
   * a section that never displays it.
   */
  enabled?: boolean;
}

/**
 * useResource loads data from the API and exposes loading, error and reload.
 *
 * It is stale-while-revalidate: `loading` is true only until there is
 * something to show. A reload, a changed dependency or a revisit refreshes in
 * the background and swaps the answer in place — never a skeleton over
 * content that is there, and an unchanged answer keeps its object identity so
 * nothing re-renders. Nothing is invented: when a refresh fails the last
 * answer stays *and* the app says so (`refreshError`, the stale notice); a
 * 4xx answer (gone, forbidden) replaces it with the error.
 *
 * `subject` names *what* the resource is about — a project, an environment.
 * When it changes, the previous subject's answer is dropped before the new
 * one is requested, so switching environment never leaves the old
 * environment's deployments, services or backups on screen looking like the
 * new one's. (A keyed resource carries its subject in the key instead.)
 */
export function useResource<T>(load: () => Promise<T>, deps: DependencyList, subject?: unknown, options: ResourceOptions = {}): Resource<T> {
  const instance = useId();
  const [nonce, setNonce] = useState(0);

  // An unkeyed resource gets a private key; a new subject gets a new one, so
  // the old subject's data is gone from the first render that has the new one.
  const subjectVersion = useRef({ subject, version: 0 });
  if (subjectVersion.current.subject !== subject) {
    subjectVersion.current = { subject, version: subjectVersion.current.version + 1 };
  }
  const key = options.key ?? `~${instance}#${subjectVersion.current.version}`;
  const owned = options.key === undefined;
  const enabled = options.enabled;

  const loader = useCallback(load, deps);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const entry = useSyncExternalStore(
    useCallback((notify) => cache.subscribe(key, notify), [key]),
    () => cache.peek<T>(key),
    () => cache.peek<T>(key),
  );

  const seen = useRef<{ key: string; nonce: number; loader: () => Promise<T> } | null>(null);
  useEffect(() => {
    const previous = seen.current;
    seen.current = { key, nonce, loader };
    // Mount or a different key: use a fresh answer if there is one, share a
    // request already on its way. Anything else (a reload, a changed
    // dependency such as a change made elsewhere) must ask again.
    if (enabled === false) return;
    const force = previous !== null && previous.key === key && (previous.nonce !== nonce || previous.loader !== loader);
    void cache.run(key, loader, { force, staleMs: options.staleMs });
  }, [key, loader, nonce, options.staleMs, enabled]);

  useEffect(() => (enabled === false ? undefined : cache.registerLoader(key, () => loaderRef.current())), [key, enabled]);
  useEffect(
    () => () => {
      if (!owned) return;
      // Forget a private entry once its component has really gone. React's
      // StrictMode unmounts and remounts at once, and the remount re-subscribes
      // before this runs, so that is not "gone" and costs no second request.
      setTimeout(() => cache.dropIfUnread(key), 0);
    },
    [key, owned],
  );

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const mutate = useCallback((data: T) => cache.setData(key, data), [key]);

  return {
    data: entry.data,
    error: entry.loaded ? null : entry.error,
    loading: !entry.loaded && (entry.fetching || entry.error === null),
    revalidating: entry.fetching && entry.loaded,
    refreshError: entry.refreshError,
    reload,
    mutate,
  };
}

/** useResource for a named read from queries.ts: its key and loader come together. */
export function useQuery<T>(query: { key: string; load: () => Promise<T> }, extraDeps: DependencyList = [], options: Omit<ResourceOptions, 'key'> = {}): Resource<T> {
  return useResource(query.load, [query.key, ...extraDeps], undefined, { ...options, key: query.key });
}

/**
 * The last answer while the next one is on its way (CO-175). A keyed resource
 * only keeps its answer for the same key, so a read whose key follows what a
 * person types starts empty on every keystroke: the list collapses to a
 * skeleton and grows back. Wrap its `data` in this to keep showing the
 * previous answer until the new one arrives; `stale` says which one it is.
 * Per component instance: a screen that remounts for another subject (a
 * product) starts clean, so one subject's answer is never shown for another.
 */
export function useKeepPrevious<T>(data: T | null | undefined): { data: T | null; stale: boolean } {
  // A resource that has not loaded yet reports null, not undefined: both mean "no answer".
  const last = useRef<T | null>(null);
  if (data != null) last.current = data;
  return { data: data ?? last.current, stale: data == null && last.current != null };
}

/** How many things on screen are showing older data because refreshing them failed. */
export function useRefreshFailures(): number {
  return useSyncExternalStore(cache.subscribeFailures, cache.failureSnapshot, cache.failureSnapshot);
}

/** slugify suggests a URL-safe slug from a display name. */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

const THEME_STORAGE_KEY = 'commerce-ops-theme';
export type ThemePreference = 'light' | 'dark' | 'system';

function readStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage can throw (private mode, blocked site data) — fall through to
    // the system default, same as index.html's anti-flash bootstrap does.
  }
  return 'system';
}

/**
 * useTheme mirrors index.html's inline anti-flash script: an explicit choice
 * is written to localStorage and applied via `data-theme` on <html>, read
 * back on every mount so every page (including AuthPage, before any
 * workspace exists) reflects the same persisted choice. "system" removes
 * the attribute entirely and lets prefers-color-scheme decide, matching
 * what a first-time visitor with no stored preference already sees.
 */
export function useTheme(): [ThemePreference, (next: ThemePreference) => void] {
  const [theme, setThemeState] = useState<ThemePreference>(readStoredTheme);

  useEffect(() => {
    if (theme === 'system') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
  }, [theme]);

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    try {
      if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Best effort only: the in-memory state still applies for this load.
    }
  }, []);

  return [theme, setTheme];
}

/**
 * useMediaQuery follows a CSS media query (CO-150): layouts that render a
 * different structure per width (a table or cards, a sidebar or a drawer)
 * render one of them, so assistive technology never meets both.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false);
  return useSyncExternalStore(subscribe, read, () => false);
}

